<script setup>
import { computed, onMounted } from "vue";
import { useProjectStore } from "@/stores/projectStore";

const store = useProjectStore();
const rowSelection = {
  onChange: () => {},
  onSelect: () => {},
  onSelectAll: () => {},
};

const columns = [
  { sorter: false },
  {
    title: "Project ID",
    dataIndex: "project_code",
    key: "project_code",
    sorter: true,
  },
  { title: "Project Name", dataIndex: "name", key: "name", sorter: true },
  {
    title: "Leader",
    dataIndex: "leader_name",
    key: "leader_name",
    sorter: true,
  },
  { title: "Team", dataIndex: "team", key: "team", sorter: false },
  { title: "Deadline", dataIndex: "deadline", key: "deadline", sorter: true },
  { title: "Priority", dataIndex: "priority", key: "priority", sorter: true },
  { title: "Status", dataIndex: "status", key: "status", sorter: true },
  { title: "", key: "action", sorter: false },
];

/** Format date like "18 Nov 2025" (fallbacks gracefully) */
function formatDateFancy(v) {
  if (!v) return "—";
  // Accept ISO or "YYYY-MM-DD"
  let d = new Date(v);
  if (isNaN(d)) {
    const short = String(v).slice(0, 10); // try YYYY-MM-DD
    const parts = short.split("-");
    if (parts.length === 3) {
      const [y, m, day] = parts;
      d = new Date(`${y}-${m}-${day}T00:00:00Z`);
    }
  }
  if (isNaN(d)) return String(v);
  return d.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}
const data = computed(() => store.items);

onMounted(() => {
  if (!store.items.length) store.list();
});

defineEmits(["edit", "delete", "view"]);
</script>

<template>
  <a-table
    class="table datatable thead-light dq-responsive-table"
    :columns="columns"
    :data-source="data"
    :row-selection="rowSelection"
    :loading="store.loading"
    rowKey="id"
  >
    <template #bodyCell="{ column, record }">
      <template v-if="column.key === 'project_code'">
        <router-link :to="`/projects/projects-details/${record.id}`">{{
          record.project_code
        }}</router-link>
      </template>

      <template v-else-if="column.key === 'name'">
        <h6 class="fw-medium">{{ record.name }}</h6>
      </template>

      <template v-else-if="column.key === 'leader_name'">
        <div class="d-flex align-items-center file-name-icon">
          <span class="avatar avatar-sm border avatar-rounded">
            <img
              v-if="record.leader_avatar"
              :src="record.leader_avatar"
              class="img-fluid"
              alt="leader"
            />
            <img
              v-else
              :src="require(`@/assets/img/users/user-01.jpg`)"
              class="img-fluid"
              alt="img"
            />
          </span>
          <div class="ms-2">
            <h6 class="fw-normal">{{ record.leader_name }}</h6>
          </div>
        </div>
      </template>

      <template v-else-if="column.key === 'team'">
        <div class="avatar-list-stacked avatar-group-sm">
          <span
            v-for="(av, i) in (record.team_avatars || []).slice(0, 3)"
            :key="i"
            class="avatar avatar-rounded"
          >
            <img
              class="border border-white"
              :src="av || require(`@/assets/img/profiles/avatar-02.jpg`)"
              alt="img"
            />
          </span>

          <!-- Only show +N if N > 0 -->
          <a
            v-if="(record.extra_team_count || 0) > 0"
            class="avatar bg-primary avatar-rounded text-fixed-white fs-12 fw-medium"
            href="javascript:void(0);"
          >
            +{{ record.extra_team_count }}
          </a>
        </div>
      </template>

      <template v-else-if="column.key === 'priority'">
        <span class="d-inline-flex align-items-center">
          <span
            class="rounded-circle d-flex justify-content-center align-items-center me-2"
            :class="{
              'bg-transparent-danger': record.priority === 'High',
              'bg-transparent-warning': record.priority === 'Medium',
              'bg-transparent-success': record.priority === 'Low',
            }"
          >
            <i
              class="ti ti-point-filled"
              :class="{
                'text-danger': record.priority === 'High',
                'text-warning': record.priority === 'Medium',
                'text-success': record.priority === 'Low',
              }"
            ></i>
          </span>
          {{ record.priority }}
        </span>
      </template>

      <template v-else-if="column.key === 'status'">
        <span
          class="d-inline-flex align-items-center badge-xs"
          :class="[
            'badge',
            record.status === 'Active' ? 'badge-success' : 'badge-danger',
          ]"
        >
          <i class="ti ti-point-filled me-1"></i>{{ record.status }}
        </span>
      </template>

      <template v-else-if="column.key === 'action'">
        <div class="action-icon d-inline-flex align-items-center">
          <a
            href="javascript:void(0);"
            class="me-2"
            @click="$emit('view', record)"
            v-can="'Project.Read'"
          >
            <i class="ti ti-eye"></i>
          </a>
          <a
            href="javascript:void(0);"
            class="me-2"
            @click="$emit('edit', record)"
            v-can="'Project.Edit'"
          >
            <i class="ti ti-edit"></i>
          </a>

          <a
            href="javascript:void(0);"
            class="me-2"
            @click="$emit('delete', record)"
            v-can="'Project.Delete'"
          >
            <i class="ti ti-trash"></i>
          </a>
        </div>
      </template>

      <template v-else-if="column.key === 'deadline'">
        {{ formatDateFancy(record.deadline || record.end_date) }}
      </template>

      <template v-else>
        {{ record[column.dataIndex] }}
      </template>
    </template>
  </a-table>
</template>

<style scoped>
@media (max-width: 767.98px) {
  :deep(.dq-responsive-table .ant-table) {
    min-width: 1040px;
  }

  :deep(.dq-responsive-table .ant-table-cell) {
    vertical-align: top;
    white-space: normal;
  }
}
</style>
